/**
 * Flashcards — drill the exact numbers (PSI, distances, percentages). Filter by
 * deck, hide the ones you know, flip to reveal, and mark known/unknown. Ports
 * renderCards. Known state flows through the progress context.
 */
import { useMemo, useState } from 'react';
import { flashcards } from '@/content/training';
import { useTrainingProgress } from '../TrainingProgressContext';
import { PageHeader, Card, Button } from './TrainingUI';

function shuffle<T>(a: T[]): T[] {
  const out = a.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

const DECKS = ['All', ...Array.from(new Set(flashcards.map((c) => c.deck)))];
const byId = Object.fromEntries(flashcards.map((c) => [c.id, c]));

export function Flashcards() {
  const progress = useTrainingProgress();
  const [deck, setDeck] = useState('All');
  const [hideKnown, setHideKnown] = useState(false);
  const [order, setOrder] = useState<string[]>(() => shuffle(flashcards.map((c) => c.id)));
  const [i, setI] = useState(0);
  const [flipped, setFlipped] = useState(false);

  const activeIds = useMemo(
    () =>
      order.filter((id) => {
        const c = byId[id];
        if (deck !== 'All' && c.deck !== deck) return false;
        if (hideKnown && progress.cardKnown[id]) return false;
        return true;
      }),
    [order, deck, hideKnown, progress.cardKnown],
  );

  const knownCount = Object.keys(progress.cardKnown).length;
  const idx = activeIds.length ? Math.min(i, activeIds.length - 1) : 0;
  const card = activeIds.length ? byId[activeIds[idx]] : null;

  const go = (delta: number) => {
    if (!activeIds.length) return;
    setI((idx + delta + activeIds.length) % activeIds.length);
    setFlipped(false);
  };

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        title="Flashcards"
        sub="The knowledge tests are full of exact numbers — PSI values, distances, percentages. These cards drill every one. Click a card to flip it."
      />

      <div className="pill-row">
        {DECKS.map((d) => (
          <button
            key={d}
            className={`pill${deck === d ? ' on' : ''}`}
            onClick={() => {
              setDeck(d);
              setI(0);
              setFlipped(false);
            }}
          >
            {d}
          </button>
        ))}
      </div>

      <label className="muted mb-4 flex items-center gap-2">
        <input
          type="checkbox"
          checked={hideKnown}
          onChange={(e) => {
            setHideKnown(e.target.checked);
            setI(0);
            setFlipped(false);
          }}
          className="accent-[#8FB560]"
        />
        Hide cards I know
      </label>

      {!card ? (
        <Card>
          <p className="muted">
            No cards here — every card in this deck is marked known. Uncheck &ldquo;hide&rdquo; or
            pick another deck.
          </p>
        </Card>
      ) : (
        <>
          <button
            className={`fc-card${flipped ? ' flipped' : ''}`}
            onClick={() => setFlipped((f) => !f)}
          >
            <div className="fc-tag">
              {card.deck}
              {progress.cardKnown[card.id] ? ' · known' : ''}
            </div>
            <div className="fc-body">{flipped ? card.back : card.front}</div>
            <div className="fc-hint">
              {flipped ? 'Click to see the question' : 'Click to reveal the answer'}
            </div>
          </button>

          <div className="fc-controls">
            <Button variant="ghost" className="small" onClick={() => go(-1)}>
              ← Prev
            </Button>
            <Button
              variant={progress.cardKnown[card.id] ? 'ghost' : 'primary'}
              className="small"
              onClick={() => {
                progress.setCardKnown(card.id, !progress.cardKnown[card.id]);
                go(1);
              }}
            >
              {progress.cardKnown[card.id] ? 'Forget it' : 'I know this'}
            </Button>
            <Button variant="ghost" className="small" onClick={() => go(1)}>
              Next →
            </Button>
            <Button
              variant="ghost"
              className="small"
              onClick={() => {
                setOrder(shuffle(flashcards.map((c) => c.id)));
                setI(0);
                setFlipped(false);
              }}
            >
              Shuffle
            </Button>
          </div>

          <div className="fc-meta">
            Card {idx + 1} of {activeIds.length} · {knownCount} of {flashcards.length} marked known
          </div>
        </>
      )}
    </div>
  );
}
